<template>
  <form class="form" novalidate @submit.prevent="handleSubmit">
    <FormRow :withThreeColumns="true">
      <FormInput :ref="addFormInputRef"
                 v-model="member.firstName"
                 :label="t('global.firstName')"
                 name="firstName"
                 type="text"
                 @validated="handleValidation"/>
      <FormInput :ref="addFormInputRef"
                 v-model="member.lastName"
                 :label="t('global.lastName')"
                 name="lastName"
                 type="text"
                 @validated="handleValidation"/>
      <FormSelect v-if="isSyndicateOrganization"
                  :ref="addFormInputRef"
                  v-model="member.jobTitle"
                  :label="t('global.jobTitle')"
                  :options="jobTitles"
                  name="jobTitle"
                  @validated="handleValidation"/>
      <FormInput v-else
                 :ref="addFormInputRef"
                 v-model="member.jobTitle"
                 :label="t('global.jobTitle')"
                 name="jobTitle"
                 type="text"
                 @validated="handleValidation"/>
    </FormRow>
    <FormRow :withThreeColumns="true">
      <FormInput :ref="addFormInputRef"
                 v-model="member.apartment"
                 :label="t('global.apartment')"
                 :rules="[]"
                 name="apartment"
                 type="text"
                 @validated="handleValidation"/>
      <FormInput :ref="addFormInputRef"
                 v-model="member.street"
                 :label="t('global.street')"
                 :list="'address-list'"
                 name="street"
                 type="text"
                 @validated="handleValidation"
                 @update:modelValue="handleInput">
        <template v-slot:before-input>
          <datalist id="address-list">
            <option v-for="address in addressOptions" :id="address.id" :key="address.id">
              {{ address.text }}
            </option>
          </datalist>
        </template>
      </FormInput>
      <FormInput :ref="addFormInputRef"
                 v-model="member.city"
                 :label="t('global.city')"
                 name="city"
                 type="text"
                 @validated="handleValidation"/>
    </FormRow>
    <FormRow :withThreeColumns="true">
      <FormInput :ref="addFormInputRef"
                 v-model="member.zipCode"
                 :label="t('global.zipCode')"
                 :rules="[required, mustMatchZipCodeFormat]"
                 name="zipCode"
                 type="zip"
                 @validated="handleValidation"/>
      <div class="form__duo-fields">
        <FormInput :ref="addFormInputRef"
                   v-model="member.phoneNumber"
                   :label="t('global.phoneNumber')"
                   :rules="[required, mustMatchPhoneNumberFormat]"
                   name="phoneNumber"
                   type="tel"
                   @validated="handleValidation"/>
        <FormInput :ref="addFormInputRef"
                   v-model="member.phoneExtension"
                   :label="t('global.phoneExtension')"
                   :rules="[]"
                   name="phoneExtension"
                   type="number"
                   @validated="handleValidation"/>
      </div>
      <FormInput :ref="addFormInputRef"
                 v-model="member.email"
                 :label="t('global.email')"
                 name="email"
                 type="text"
                 @validated="handleValidation"/>
    </FormRow>
    <button class="form__submit btn btn--fullscreen">{{ t('global.save') }}</button>
  </form>
</template>


<script lang="ts" setup>
import {ref} from "vue"
import {useI18n} from "vue3-i18n"
import {notifyError} from "@/notify"
import {mustMatchPhoneNumberFormat, mustMatchZipCodeFormat, required} from "@/validation/rules"
import {Status} from "@/validation"
import {Member} from "@/types/entities/member"
import FormRow from "@/components/forms/FormRow.vue"
import FormInput from "@/components/forms/FormInput.vue"
import FormSelect from "@/components/forms/FormSelect.vue";
import {useAddressService} from "@/inversify.config";
import {IFilterAddressesRequest, IGetAddressRequest} from "@/types/requests";
import {IAddress} from "@/types/responses";

// eslint-disable-next-line no-undef
const props = defineProps<{
  member?: Member,
  isSyndicateOrganization?: boolean
}>()

// eslint-disable-next-line
const emit = defineEmits<{
  (event: "formSubmit", member: Member): void
}>()

const {t, getLocale} = useI18n()
const addressService = useAddressService()

const addressOptions = ref<IAddress[]>([])
const member = ref<Member>(props.member ?? {})

const jobTitles = [
  {
    label: t('member.jobTitle.administrator.label'),
    name: t('member.jobTitle.administrator.value')
  },
  {
    label: t('member.jobTitle.manager.label'),
    name: t('member.jobTitle.manager.value')
  },
  {
    label: t('member.jobTitle.coowner.label'),
    name: t('member.jobTitle.coowner.value')
  },
]

const formInputs = ref<(typeof FormInput)[]>([])
const inputValidationStatuses: any = {}

function addFormInputRef(ref: typeof FormInput) {
  if (!formInputs.value.includes(ref))
    formInputs.value.push(ref)
}

async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid
}

async function handleSubmit() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())
  if (Object.values(inputValidationStatuses).some(x => x === false)) {
    notifyError(t('global.formErrorNotification'))
    return
  }
  emit("formSubmit", member.value)
}

async function handleInput(inputValue: string) {
  let selectedOptions = addressOptions.value.filter(x => x.text == inputValue)
  if (selectedOptions.length > 0) {
    let request = {
      id: selectedOptions[0].id,
      language: getLocale()
    } as IGetAddressRequest
    let response = await addressService.getAddress(request)
    member.value.street = response.detailedAddress.street
    member.value.city = response.detailedAddress.city
    member.value.zipCode = response.detailedAddress.zipCode.replace(' ', '')
    return;
  }
  await loadMatchingAddresses(inputValue)
}

async function loadMatchingAddresses(inputValue: string) {
  let request = {
    street: inputValue,
    language: getLocale()
  } as IFilterAddressesRequest
  let addresses = await addressService.filterAddresses(request)
  addressOptions.value = addresses.matchingAddresses
}
</script>
